/**
 * Build the `native_filters` URL parameter that Superset's `/embedded/<id>`
 * route uses to preselect a native time-range filter on first render.
 *
 * Superset's filter system has two URL conventions:
 *   1. `native_filters_key=<short hash>` — a reference to a backend-cached
 *      filter state. This is what Superset itself writes when a user
 *      manipulates filters in the dashboard UI, because filter state can
 *      grow past URL-length limits.
 *   2. `native_filters=<rison>` — inline rison-encoded filter state. Read
 *      on first load when no `_key` is present. This is the only path
 *      available to an embed host (the SPA) since we have no way to
 *      pre-cache state through the guest-token API.
 *
 * Rison is a URL-friendly JSON dialect (https://github.com/Nanonid/rison).
 * The subset we need is tiny — one outer object keyed by filter id, each
 * value an object with `extraFormData` and `filterState`, both carrying
 * the same `time_range` / `value` string. Encoding rules used here:
 *   - object  →  `(k1:v1,k2:v2)`
 *   - string with rison-reserved chars (`,()'!:` or space) → single-quoted
 *     with `'` and `!` escaped as `!'` and `!!`
 *   - plain identifier string → unquoted
 *
 * The encoder is deliberately narrow: it handles exactly the shapes used
 * for time-range native filters and rejects anything else with a thrown
 * error. Broadening it without a real call site is YAGNI; if a future
 * filter type needs different shapes, encode it explicitly there.
 */

/**
 * Canonical id used by provisioned dashboards. Self-serve Superset boards can
 * carry generated ids; the backend reports those per dashboard. This constant
 * remains only as a rolling-deploy fallback for responses from older backends.
 */
export const TIME_RANGE_NATIVE_FILTER_ID = "NATIVE_FILTER-time-range";

export function resolveDashboardTimeFilterId(
  filterId: string | null | undefined
): string | null {
  return filterId === undefined ? TIME_RANGE_NATIVE_FILTER_ID : filterId;
}

type RisonValue = string | RisonObject;
interface RisonObject {
  [key: string]: RisonValue;
}

const RISON_RESERVED = /[\s,()':!]/;

function encodeRisonString(value: string): string {
  // Plain identifier — no escaping needed.
  if (!RISON_RESERVED.test(value)) return value;
  // Quoted string: ' and ! are escaped with rison's `!` escape character
  // (rison has no backslash escape; `!` itself is the escape, so it doubles
  // to `!!` and a single quote becomes `!'`).
  const escaped = value.replace(/!/g, "!!").replace(/'/g, "!'");
  return `'${escaped}'`;
}

function encodeRisonValue(value: RisonValue): string {
  if (typeof value === "string") return encodeRisonString(value);
  if (value && typeof value === "object") {
    const parts = Object.entries(value).map(
      ([k, v]) => `${encodeRisonString(k)}:${encodeRisonValue(v)}`
    );
    return `(${parts.join(",")})`;
  }
  throw new Error(
    `supersetNativeFilters: unsupported rison value type ${typeof value}`
  );
}

interface TimeRangeFilterArgs {
  /** Native filter id as stored on the dashboard, e.g. `NATIVE_FILTER-time-range`. */
  filterId: string;
  /** ISO `YYYY-MM-DD` lower bound (inclusive). */
  from: string;
  /** ISO `YYYY-MM-DD` upper bound (inclusive in Superset's parser). */
  to: string;
}

/**
 * Build the value for the `native_filters` URL parameter that preselects
 * a Superset `filter_time` native filter to ``[from, to]``.
 *
 * The dataMask shape (`extraFormData.time_range`, `filterState.value`) is
 * what Superset's frontend produces internally when the user picks a range
 * in the dashboard's filter UI — replicating it inline gives the embed the
 * same effect on first render. `extraFormData.time_range` is the field the
 * query layer reads to override each chart's own time_range; `filterState`
 * drives the filter-pill UI label so the partner can see what's applied.
 */
export function buildTimeRangeNativeFilter({
  filterId,
  from,
  to,
}: TimeRangeFilterArgs): string {
  const range = `${from} : ${to}`;
  const dataMask: RisonObject = {
    [filterId]: {
      extraFormData: { time_range: range },
      filterState: { value: range },
    },
  };
  return encodeRisonValue(dataMask);
}

// Exposed only for unit testing the rison encoder in isolation.
export const __test__ = { encodeRisonString, encodeRisonValue };
